<template>
  <div class="table-responsive" :class="{ 'tbl-inherit': overflowState }">
    <div style="width: 100%; overflow-x: visible;">
      <table
        v-if="!loading && !hasNoLists"
        id="under-review-table"
        class="table has-review-date"
        style="table-layout: fixed !important; width: 100%;"
      >
        <ApplicantTableHeader
          :sort-key="sortKey"
          :asc="asc"
          :client-status="getClientStatus"
          :showReviewListDate="true"
          :showCheckbox="false"
          @sort="sorting"
          @search="search"
          @check-all="checkAll"
          @overflow="overflow"
        />
        <tbody>
          <ApplicantRow
            v-for="(applicant, index) in applicants"
            v-bind:key="applicant.id"
            :applicant="applicant"
            :applicantIndex="index"
            :rowNumber="rowNumber(index)"
            @hidden="overflowState = 0"
            @shown="overflow"
            @changed="checkUncheckJobseeker"
            :isPendingReview="true"
            :showCheckbox="false"
          />
          <!-- Keeps columns visible on zero results, matching Settings > Lists. -->
          <tr v-if="showEmptyMessage">
            <td colspan="100%" class="text-center">
              登録者が見つかりませんでした
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loading && hasNoLists" class="under-review-empty text-center">
        あなたに閲覧権限が付与されているリストに、ステータス「確認中」の登録者がいる場合に、<br>
        この画面に表示されます。
      </div>
    </div>

    <div class="pagination-area under-review-pagination" v-if="totalPages > 1">
      <paginate :total-page="totalPages" :callback="changePage" prev-text="‹" next-text="›" />
    </div>
    <Loader v-show="loading" />
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState, mapGetters } from 'vuex';
import Loader from '@/components/utils/Loader.vue';
import Paginate from '@/components/utils/Paginate.vue';
import actions from '@/config/actions';
import mutations from '@/config/mutations';
import ApplicantRow from './ApplicantRow.vue';
import ApplicantTableHeader from './ApplicantTableHeader.vue';

export default {
  name: 'UnderReviewList',
  components: {
    Paginate, Loader, ApplicantRow, ApplicantTableHeader,
  },
  data() {
    return {
      sortKey: null,
      asc: true,
      overflowState: 0,
      showEmptyMessage: false,
      loadInFlight: false,
      pendingLoadParams: null,
    };
  },
  async created() {
    // Set before the first await — otherwise there's a window where loading is still the
    // store's default (false) and showEmptyMessage is still its initial false, so the table
    // (with a real header but zero rows) flashes visible until the fetch actually resolves.
    this.setLoading(true);
    // Default the status filter to 確認中 so the header dropdown reflects what's actually
    // shown; buildParams() only falls back to this when no status has been chosen yet, so
    // picking other statuses from the dropdown overrides it (see search()/buildParams()).
    this.setStatusModel(['確認中']);
    // Unlike /applicant, this page's search bar doesn't mount SearchForm.vue, which is the
    // only other place selectors get fetched — so the 直近職種 dropdown's occupations list
    // load here explicitly.
    this[actions.applicant.LOAD_SELECTORS]()
      .then(({ data }) => {
        this[mutations.applicant.SET_SELECTORS](data.data);
      })
      .catch(error => console.log(error));
    await this.loadUnderReviewSummary();
    this.load(this.buildParams(1));
  },
  computed: {
    ...mapGetters({
      getClientStatus: 'getClientStatus',
      selectedReviewListIds: 'getSelectedReviewListIds',
      underReviewSummary: 'getUnderReviewSummary',
      isReviewer: 'isCreateReviewUser',
      childJobModel: 'getChildJobModel',
      mainJobModel: 'getMainJobModel',
      companyModel: 'getCompanyModel',
      colorModel: 'getColorModel',
      scoutHope: 'getScoutHope',
      englishSkillModel: 'getEnglishSkillModel',
      statusModel: 'getStatusModel',
    }),
    hasNoLists() {
      return (this.underReviewSummary.lists || []).length === 0;
    },
    ...mapState({
      applicants: ({ applicant }) => applicant.applicants,
      pagination: ({ applicant }) => applicant.pagination,
      reloadApplicants: ({ applicant }) => applicant.reloadApplicants,
      sortBy: ({ applicant }) => applicant.sortBy,
      page: ({ applicant }) => applicant.page,
      favoritesOnly: ({ applicant }) => applicant.favoritesOnly,
      loading: ({ applicant }) => applicant.loading,
      isCheckAll: ({ applicant }) => applicant.isCheckAll,
      jobseekerIds: ({ applicant }) => applicant.jobseekerIds,
      searchParams: ({ applicant }) => applicant.searchParams,
    }),
    totalPages() {
      const { pagination } = this;
      return pagination.total ? Math.ceil(pagination.total / pagination.perPage) : 0;
    },
    from() {
      return ((parseInt(this.pagination.current, 10) - 1) * this.pagination.perPage) + 1;
    },
    to() {
      const priorPages = (parseInt(this.pagination.current, 10) - 1) * this.pagination.perPage;
      return priorPages + this.applicants.length;
    },
  },
  methods: {
    ...mapActions([actions.applicant.LOAD_APPLICANTS, actions.applicant.LOAD_SELECTORS, 'loadUnderReviewSummary']),
    ...mapMutations([
      mutations.applicant.SET_APPLICANTS,
      mutations.applicant.CHANGE_SORT_COLUMN,
      mutations.applicant.CHANGE_SEARCH_PARAMS,
      mutations.applicant.SET_SELECTORS,
      'setApplicantCounts',
      'setPage',
      'setReloadApplicants',
      'setLoading',
      'setCheckAll',
      'setResetCheckAll',
      'setPagination',
      'setResetApplicantIds',
      'setCurrentPageApplicants',
      'setMainJobModel',
      'setChildJobModel',
      'setCompanyModel',
      'setColorModel',
      'setScoutHope',
      'setEnglishSkillModel',
      'setStatusModel',
    ]),
    overflow() {
      this.overflowState = 1;
    },
    checkAll() {
      this.setCheckAll(!this.isCheckAll);
    },
    checkUncheckJobseeker(id) {
      const index = this.jobseekerIds.indexOf(id);
      if (index > -1) {
        this.jobseekerIds.splice(index, 1);
      } else {
        this.jobseekerIds.push(id);
      }
    },
    sorting(key) {
      let column = '';
      if (this.sortKey !== null && this.sortKey === key) {
        column = this.asc ? key : `-${key}`;
        this.asc = !this.asc;
      } else {
        column = `-${key}`;
        this.asc = true;
      }
      this.sortKey = key;
      this[mutations.applicant.CHANGE_SORT_COLUMN](column);
    },
    async load(params = {}) {
      // created() can fire two loads on mount; coalesce so only one is ever in flight.
      if (this.loadInFlight) {
        this.pendingLoadParams = params;
        return;
      }
      this.loadInFlight = true;
      try {
        this.setLoading(true);
        const { data } = await this[actions.applicant.LOAD_APPLICANTS](params);
        this[mutations.applicant.SET_APPLICANTS](data.data);
        this.setResetCheckAll();
        this.setResetApplicantIds();
        this.setCurrentPageApplicants(data.data);
        this.showEmptyMessage = this.applicants.length === 0;
        this.setPagination(data.meta.pagination || {});
        this.showcount();
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
        this.setReloadApplicants(false);
        this.loadInFlight = false;
        if (this.pendingLoadParams) {
          const nextParams = this.pendingLoadParams;
          this.pendingLoadParams = null;
          this.load(nextParams);
        }
      }
    },
    buildParams(page = null) {
      // underReviewMenu tells the backend this is the 確認中 page — list-permission scoping
      // applies for every role here, unlike the Registrant List page which shares this same
      // API but lets Admin/HR/operation roles see the whole company by default.
      const params = { isNewPanel: true, rowPerPage: 100, underReviewMenu: true };
      if (this.searchParams && Object.keys(this.searchParams).length > 0) {
        Object.assign(params, this.searchParams);
      }
      // Default to 確認中 only when the header dropdown hasn't set an explicit status
      // filter yet (see search()) — otherwise the user's selection would never take effect.
      if (!params.status) {
        params.status = ['確認中'];
      }
      if (page) params.page = page;
      if (this.sortBy) params.sort = this.sortBy;
      if (this.favoritesOnly) params.favorites = 1;
      // No selection == show everything this account is permitted to see (backend default);
      // an explicit selection narrows to just those lists.
      if (this.selectedReviewListIds && this.selectedReviewListIds.length) {
        params.review_list_ids = this.selectedReviewListIds;
      }
      return params;
    },
    search() {
      const params = {};
      Object.assign(params, this.searchParams);
      params.childOccupations = this.childJobModel;
      params.mainJobs = this.mainJobModel;
      params.englishSkills = this.englishSkillModel;
      params.regcompany = this.companyModel;
      params.client_color = this.colorModel;
      params.scoutHope = this.scoutHope;
      params.status = this.statusModel;
      if (params.childOccupations.length === 0) delete params.childOccupations;
      if (params.mainJobs.length === 0) delete params.mainJobs;
      if (params.englishSkills.length === 0) delete params.englishSkills;
      this[mutations.applicant.CHANGE_SEARCH_PARAMS](params);
      this.overflowState = 0;
    },
    resetFilters() {
      // Switching which review list is displayed is a fresh context — a 経由/直近職種
      // selection from the previous list shouldn't silently keep filtering the new one.
      this.setMainJobModel([]);
      this.setChildJobModel([]);
      this.setCompanyModel([]);
      this.setColorModel([]);
      this.setScoutHope(null);
      this.setEnglishSkillModel([]);
      this[mutations.applicant.CHANGE_SEARCH_PARAMS]({});
    },
    changePage(page) {
      this.setPage(page);
    },
    rowNumber(index) {
      return index + 1 + ((parseInt(this.pagination.current, 10) - 1) * this.pagination.perPage);
    },
    showcount() {
      this.setApplicantCounts({
        total: this.pagination.total,
        from: this.from,
        to: this.to,
        perPage: this.pagination.perPage,
      });
    },
  },
  watch: {
    reloadApplicants() {
      if (this.page === 1) this.load(this.buildParams(1));
      else this.setPage(1);
    },
    selectedReviewListIds() {
      // Resetting filters commits an empty searchParams, which the searchParams watcher
      // below picks up and reloads from — no separate load() call needed here.
      this.resetFilters();
    },
    searchParams() {
      if (this.page === 1) this.load(this.buildParams(1));
      else this.setPage(1);
    },
    favoritesOnly() {
      if (this.page === 1) this.load(this.buildParams(1));
      else this.setPage(1);
    },
    page(currentPage) {
      this.load(this.buildParams(currentPage));
    },
    sortBy() {
      if (this.page === 1) this.load(this.buildParams(1));
      else this.setPage(1);
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: visible;
}

.under-review-empty {
  padding: 100px 20px;
  color: #3c3c3c;
  font-size: 14px;
}

::v-deep .under-review-pagination .pagination {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
}

::v-deep .under-review-pagination .page-item {
  list-style: none;
}

::v-deep .under-review-pagination .page-link {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border: none;
  background: transparent;
  color: #3c3c3c;
  border-radius: 6px;
  text-decoration: none;
  font-size: 14px;
}

::v-deep .under-review-pagination .page-item.active .page-link {
  background-color: #e5e7eb;
  color: #3c3c3c;
  font-weight: bold;
}

::v-deep .under-review-pagination .page-item.disabled .page-link {
  color: #c6c6c6;
}
</style>

<style>
#under-review-table {
  border-collapse: collapse;
}

/* box-shadow instead of border-bottom: table-cell borders can silently lose out to
   border-collapse's cross-row merging rules; an inset box-shadow draws inside the cell
   itself and isn't subject to that algorithm, so it reliably shows regardless. */
#under-review-table .applicant-list td {
  box-shadow: inset 0 -1px 0 0 #c6c6c6 !important;
}
</style>
